import type { ImageUrlOverrides } from "@hightouch/core/content/types";

export const CONTENT_NODE_URL_PATTERN =
  /\/content_nodes\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.\w+/i;

/**
 * Reverse-map an external DAM URL back to the CDN URL.
 * Returns the original URL unchanged if no mapping exists.
 */
export function reverseLookupImageUrl(
  src: string,
  overrides: ImageUrlOverrides | null | undefined,
): string {
  if (!overrides) return src;
  const entry = Object.entries(overrides).find(([, ext]) => ext === src);
  return entry ? entry[0] : src;
}

/**
 * Swap an edited image's URL in `html`. `from` is the CDN URL the editor ran
 * against, but the html may hold the DAM URL instead, and the URL alone doesn't
 * say which — so replace both forms. The absent one is a no-op.
 */
export function replaceImageUrl(
  html: string,
  from: string,
  to: string,
  overrides: ImageUrlOverrides | null | undefined,
): string {
  const patched = html.replaceAll(from, to);
  const externalUrl =
    overrides && Object.hasOwn(overrides, from) ? overrides[from] : undefined;
  return externalUrl ? patched.replaceAll(externalUrl, to) : patched;
}

/**
 * Check if a URL is a content node image — either a CDN URL matching the
 * content_nodes pattern, or an external DAM URL present in the overrides map.
 */
export function isContentNodeImageUrl(
  src: string,
  overrides: ImageUrlOverrides | null | undefined,
): boolean {
  if (CONTENT_NODE_URL_PATTERN.test(src)) return true;
  if (!overrides) return false;
  return Object.values(overrides).includes(src);
}
